/**
 * form-analytics.ts — Lane COLLECT (tracking v3, `.claude/handoffs/tracking-v3-design.md`).
 *
 * Per-field form funnel instrumentation: which fields visitors actually
 * engage with, and which they abandon empty — feeding `landing_events` via
 * the v2 beacon pipeline (`trackEvent` in `./engagement.ts`).
 *
 * - **`field_focus`**: fires once per field, on its FIRST focus.
 * - **`field_abandon`**: fires once per field, on a blur where the field is
 *   still empty. "Fire at most once per field per session; a later filled
 *   blur upgrades nothing" (design contract) — this is intentionally dumb:
 *   once a field has recorded an abandon, it never sends anything else for
 *   that field again this mount, even if the visitor later fills it in and
 *   blurs again.
 *
 * NEVER reads or stores field VALUES — only presence booleans (`value.trim()
 * .length === 0`, `checked`), never the string/boolean content itself.
 *
 * `focusin`/`focusout` (not `focus`/`blur`) are used because the latter
 * don't bubble — one delegated pair of listeners on the form element covers
 * every field without per-field listener wiring.
 *
 * CROSS-LANE NOTE: same as web-vitals.ts/friction.ts — `'form_field'` isn't
 * in `trackEvent`'s `TrackEventType` union yet (out of this lane's
 * file-ownership list); cast at the call site, flagged to the session lead.
 *
 * Related: [[handoffs/tracking-v3-design]] · ./engagement.ts
 */

import { trackEvent, type TrackEventType, type TrackEventMeta } from './engagement';

/** Contract bound: short label/section strings ≤256 chars. */
const LABEL_MAX_LEN = 256;

/**
 * `trackEvent`'s `type`/`meta` params (`./engagement.ts`, out of this lane's
 * ownership) are still typed for the v2 5-type registry only — see the
 * CROSS-LANE NOTE in the module docstring. The double-cast through `unknown`
 * sends every real field over the wire untouched (JS doesn't enforce TS
 * shapes at runtime); it exists purely so THIS lane's own call sites stay
 * checked against the v3 meta shape the design contract actually specifies.
 */
function sendFormFieldEvent(
  name: 'field_focus' | 'field_abandon',
  meta: { label: string; section: string; path?: string; state?: string }
): void {
  trackEvent('form_field' as TrackEventType, name, meta as unknown as TrackEventMeta);
}

type NamedFormField = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function isNamedFormField(el: Element): el is NamedFormField {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}

/** "named/id'd field" (design contract) — falls back to `id` when `name` is
 * absent; a field with neither is not individually trackable and is skipped. */
function fieldKeyFor(el: NamedFormField): string | null {
  const key = el.name || el.id;
  return key ? key : null;
}

/** Presence-only check — NEVER inspects/stores the actual value string. */
function isFieldEmpty(el: NamedFormField): boolean {
  if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
    return !el.checked;
  }
  return el.value.trim().length === 0;
}

function currentPath(): string | undefined {
  return typeof location !== 'undefined' ? location.pathname : undefined;
}

/**
 * Wires per-field `field_focus`/`field_abandon` telemetry onto `form`.
 * Returns a cleanup function that removes the listeners. Each call gets its
 * own independent focused/abandoned tracking sets (scoped to the closure),
 * so mounting the same form twice (or remounting) never leaks state across
 * instances.
 */
export function attachFormAnalytics(form: HTMLFormElement, formName: string): () => void {
  if (typeof window === 'undefined') return () => {};

  const focusedFields = new Set<string>();
  const abandonFiredFields = new Set<string>();
  const section = formName.slice(0, LABEL_MAX_LEN);

  function handleFocusIn(e: FocusEvent): void {
    const target = e.target;
    if (!(target instanceof Element) || !isNamedFormField(target)) return;
    const key = fieldKeyFor(target);
    if (!key) return;
    if (focusedFields.has(key)) return; // already fired for this field this mount

    focusedFields.add(key);
    sendFormFieldEvent('field_focus', {
      label: key.slice(0, LABEL_MAX_LEN),
      section,
      path: currentPath(),
    });
  }

  function handleFocusOut(e: FocusEvent): void {
    const target = e.target;
    if (!(target instanceof Element) || !isNamedFormField(target)) return;
    const key = fieldKeyFor(target);
    if (!key) return;
    if (abandonFiredFields.has(key)) return; // fire at most once per field per session
    if (!isFieldEmpty(target)) return; // filled — not an abandon

    abandonFiredFields.add(key);
    sendFormFieldEvent('field_abandon', {
      label: key.slice(0, LABEL_MAX_LEN),
      section,
      path: currentPath(),
      state: 'empty',
    });
  }

  form.addEventListener('focusin', handleFocusIn);
  form.addEventListener('focusout', handleFocusOut);

  return () => {
    form.removeEventListener('focusin', handleFocusIn);
    form.removeEventListener('focusout', handleFocusOut);
  };
}
